import {
  Image as ChakraImage,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';
import { ReactNode } from 'react';

export interface ProofPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileUrl: string;
  renderPdfPreview?: (fileUrl: string) => ReactNode;
  zIndex?: number;
}

function isPdfUrl(url: string): boolean {
  try {
    return new URL(url).pathname.toLowerCase().endsWith('.pdf');
  } catch {
    return url.split('?')[0].toLowerCase().endsWith('.pdf');
  }
}

export function ProofPreviewModal({
  isOpen,
  onClose,
  fileUrl,
  renderPdfPreview,
  zIndex = 1600,
}: ProofPreviewModalProps) {
  const isPdf = isPdfUrl(fileUrl);
  const contentZIndex = zIndex + 1;
  const closeButtonZIndex = zIndex + 2;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc
      closeOnOverlayClick
      scrollBehavior="outside"
      size={isPdf ? '3xl' : 'full'}
      isCentered
      blockScrollOnMount
    >
      <ModalOverlay bg="blackAlpha.800" zIndex={zIndex} />
      <ModalContent
        containerProps={{ zIndex: contentZIndex }}
        zIndex={contentZIndex}
        bg={isPdf ? undefined : 'transparent'}
        boxShadow={isPdf ? undefined : 'none'}
        h={isPdf ? undefined : '100dvh'}
        maxH={isPdf ? undefined : '100dvh'}
        maxW={isPdf ? undefined : '100vw'}
        p={isPdf ? '0' : { base: '56px 16px 24px', md: '72px 48px 32px' }}
      >
        <ModalCloseButton
          bg="white"
          borderRadius="full"
          boxShadow="md"
          color="gray.700"
          right={{ base: 4, md: 6 }}
          top={{ base: 4, md: 6 }}
          zIndex={closeButtonZIndex}
        />
        <ModalBody
          alignItems="center"
          display="flex"
          justifyContent="center"
          margin="auto"
          overflow={isPdf ? 'auto' : 'hidden'}
          p="0"
          w="full"
        >
          {isPdf
            ? renderPdfPreview?.(fileUrl)
            : fileUrl && (
                <ChakraImage
                  src={fileUrl}
                  alt="preview-image"
                  display="block"
                  maxH={{
                    base: 'calc(100dvh - 80px)',
                    md: 'calc(100dvh - 104px)',
                  }}
                  maxW={{
                    base: 'calc(100vw - 32px)',
                    md: 'calc(100vw - 96px)',
                  }}
                  objectFit="contain"
                  w="auto"
                />
              )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
